import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import ImageUpload from "../components/ImageUpload";
import { ArrowLeft, Loader2, Store, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "restaurantes", label: "Restaurantes" },
  { key: "hoteles", label: "Hoteles" },
  { key: "cafes", label: "Cafés" },
  { key: "bares", label: "Bares" },
  { key: "tours", label: "Tours" },
  { key: "car_wash", label: "Car Wash" },
  { key: "mecanicos", label: "Mecánicos" },
  { key: "salud_belleza", label: "Salud y Belleza" },
  { key: "entretenimiento", label: "Entretenimiento" },
  { key: "otros", label: "Otros" },
];

const DAYS = [
  { key: "lunes", label: "Lunes" },
  { key: "martes", label: "Martes" },
  { key: "miercoles", label: "Miércoles" },
  { key: "jueves", label: "Jueves" },
  { key: "viernes", label: "Viernes" },
  { key: "sabado", label: "Sábado" },
  { key: "domingo", label: "Domingo" },
];

const INITIAL = {
  name: "",
  owner_name: "",
  email: "",
  phone: "",
  whatsapp: "",
  category: "",
  description: "",
  address: "",
  city: "",
  state: "",
  maps_link: "",
  website: "",
  social_media: { instagram: "", facebook: "", tiktok: "" },
  hours: {},
  discount_percent: "",
  discount_details: "",
  discount_terms: "",
  discount_expires_at: "",
  rfc: "",
  photos: [],
  logo_url: "",
  verification_doc_url: "",
};

export default function GuiaRegisterBusinessPage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...INITIAL, owner_name: user?.name || "", email: user?.email || "" });
  const [submitting, setSubmitting] = useState(false);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-6">
        <div className="card-base text-center max-w-md">
          <Store className="w-12 h-12 text-terracotta mx-auto mb-3" />
          <h2 className="font-heading text-xl text-agave mb-2">Inicia sesión</h2>
          <p className="text-neutral-600 mb-4">
            Necesitas una cuenta para registrar tu negocio en la Guía RivoNes.
          </p>
          <Link
            to="/login"
            state={{ from: "/guia/registrar" }}
            className="btn-primary inline-flex"
            data-testid="biz-register-login-link"
          >
            Iniciar sesión
          </Link>
        </div>
      </div>
    );
  }

  const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const updateSocial = (key, value) =>
    setForm((f) => ({ ...f, social_media: { ...f.social_media, [key]: value } }));
  const updateHours = (key, value) =>
    setForm((f) => ({ ...f, hours: { ...f.hours, [key]: value } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.description.trim().length < 20) {
      toast.error("La descripción debe tener al menos 20 caracteres");
      return;
    }
    if (!form.category) {
      toast.error("Selecciona una categoría");
      return;
    }
    const payload = {
      ...form,
      discount_percent: form.discount_percent === "" ? null : Number(form.discount_percent),
      discount_expires_at: form.discount_expires_at || null,
      // Strip empty social entries
      social_media: Object.fromEntries(
        Object.entries(form.social_media).filter(([, v]) => v && v.trim()),
      ),
      // Strip empty hours
      hours: Object.fromEntries(
        Object.entries(form.hours).filter(([, v]) => v && v.trim()),
      ),
    };
    setSubmitting(true);
    try {
      const { data } = await api.post("/guia/businesses", payload);
      toast.success("¡Negocio enviado! Te notificaremos cuando sea aprobado.");
      navigate(`/guia/${data.business_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo registrar el negocio");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand pb-12" data-testid="biz-register-page">
      <div className="container-main py-6 max-w-2xl">
        <Link
          to="/guia"
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-agave mb-4"
          data-testid="biz-register-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a la Guía
        </Link>

        <h1 className="text-3xl font-heading font-bold text-agave mb-1">Registra tu negocio</h1>
        <p className="text-neutral-600 mb-6">
          Llega a miles de viajeros con un descuento exclusivo para miembros RivoNes.
          Tu listado será revisado por el equipo antes de publicarse.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="biz-register-form">
          {/* Section: Basic info */}
          <section className="card-base space-y-4">
            <h2 className="font-heading font-semibold text-lg text-agave">Información básica</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="biz-name">Nombre del negocio *</Label>
                <Input
                  id="biz-name"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  required
                  data-testid="biz-field-name"
                />
              </div>
              <div>
                <Label htmlFor="biz-category">Categoría *</Label>
                <Select value={form.category} onValueChange={(v) => updateField("category", v)}>
                  <SelectTrigger data-testid="biz-field-category">
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.key} value={c.key}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="biz-owner">Nombre del dueño *</Label>
                <Input
                  id="biz-owner"
                  value={form.owner_name}
                  onChange={(e) => updateField("owner_name", e.target.value)}
                  required
                  data-testid="biz-field-owner"
                />
              </div>
              <div>
                <Label htmlFor="biz-email">Email de contacto *</Label>
                <Input
                  id="biz-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  required
                  data-testid="biz-field-email"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="biz-description">Descripción *</Label>
              <Textarea
                id="biz-description"
                rows={4}
                placeholder="Cuenta a los viajeros qué hace especial a tu negocio. Mínimo 20 caracteres."
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                required
                data-testid="biz-field-description"
              />
              <p className="text-xs text-neutral-500 mt-1">{form.description.length} caracteres</p>
            </div>
          </section>

          {/* Section: Location */}
          <section className="card-base space-y-4">
            <h2 className="font-heading font-semibold text-lg text-agave">Ubicación</h2>
            <div>
              <Label htmlFor="biz-address">Dirección *</Label>
              <Input
                id="biz-address"
                value={form.address}
                onChange={(e) => updateField("address", e.target.value)}
                required
                data-testid="biz-field-address"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="biz-city">Ciudad *</Label>
                <Input
                  id="biz-city"
                  value={form.city}
                  onChange={(e) => updateField("city", e.target.value)}
                  required
                  data-testid="biz-field-city"
                />
              </div>
              <div>
                <Label htmlFor="biz-state">Estado</Label>
                <Input
                  id="biz-state"
                  value={form.state}
                  onChange={(e) => updateField("state", e.target.value)}
                  data-testid="biz-field-state"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="biz-maps">Enlace de Google Maps</Label>
              <Input
                id="biz-maps"
                placeholder="https://maps.app.goo.gl/..."
                value={form.maps_link}
                onChange={(e) => updateField("maps_link", e.target.value)}
                data-testid="biz-field-maps"
              />
            </div>
          </section>

          {/* Section: Contact */}
          <section className="card-base space-y-4">
            <h2 className="font-heading font-semibold text-lg text-agave">Contacto</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="biz-phone">Teléfono *</Label>
                <Input
                  id="biz-phone"
                  type="tel"
                  placeholder="+52 664 311 2233"
                  value={form.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  required
                  data-testid="biz-field-phone"
                />
              </div>
              <div>
                <Label htmlFor="biz-wa">WhatsApp</Label>
                <Input
                  id="biz-wa"
                  type="tel"
                  value={form.whatsapp}
                  onChange={(e) => updateField("whatsapp", e.target.value)}
                  data-testid="biz-field-whatsapp"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="biz-web">Sitio web</Label>
              <Input
                id="biz-web"
                placeholder="https://mi-negocio.mx"
                value={form.website}
                onChange={(e) => updateField("website", e.target.value)}
                data-testid="biz-field-website"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="biz-ig">Instagram</Label>
                <Input
                  id="biz-ig"
                  placeholder="@usuario"
                  value={form.social_media.instagram}
                  onChange={(e) => updateSocial("instagram", e.target.value)}
                  data-testid="biz-field-instagram"
                />
              </div>
              <div>
                <Label htmlFor="biz-fb">Facebook</Label>
                <Input
                  id="biz-fb"
                  placeholder="página o usuario"
                  value={form.social_media.facebook}
                  onChange={(e) => updateSocial("facebook", e.target.value)}
                  data-testid="biz-field-facebook"
                />
              </div>
              <div>
                <Label htmlFor="biz-tt">TikTok</Label>
                <Input
                  id="biz-tt"
                  value={form.social_media.tiktok}
                  onChange={(e) => updateSocial("tiktok", e.target.value)}
                  data-testid="biz-field-tiktok"
                />
              </div>
            </div>
          </section>

          {/* Section: Discount */}
          <section className="card-base space-y-4">
            <h2 className="font-heading font-semibold text-lg text-agave">Descuento para miembros RivoNes</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="biz-disc">% de descuento</Label>
                <Input
                  id="biz-disc"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  placeholder="15"
                  value={form.discount_percent}
                  onChange={(e) => updateField("discount_percent", e.target.value)}
                  data-testid="biz-field-discount"
                />
              </div>
              <div>
                <Label htmlFor="biz-disc-exp">Vence (opcional)</Label>
                <Input
                  id="biz-disc-exp"
                  type="date"
                  value={form.discount_expires_at}
                  onChange={(e) => updateField("discount_expires_at", e.target.value)}
                  data-testid="biz-field-discount-exp"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="biz-disc-det">Detalles del descuento</Label>
              <Input
                id="biz-disc-det"
                placeholder="Ej. 15% off en consumo total"
                value={form.discount_details}
                onChange={(e) => updateField("discount_details", e.target.value)}
                data-testid="biz-field-discount-details"
              />
            </div>
            <div>
              <Label htmlFor="biz-disc-terms">Términos y condiciones</Label>
              <Textarea
                id="biz-disc-terms"
                rows={2}
                placeholder="Ej. Solo lunes a jueves, no acumulable con otras promociones."
                value={form.discount_terms}
                onChange={(e) => updateField("discount_terms", e.target.value)}
                data-testid="biz-field-discount-terms"
              />
            </div>
          </section>

          {/* Section: Hours */}
          <section className="card-base space-y-3">
            <h2 className="font-heading font-semibold text-lg text-agave">Horarios</h2>
            <p className="text-xs text-neutral-500">Ejemplo: 9:00 - 22:00 · Déjalo vacío si está cerrado.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {DAYS.map((d) => (
                <div key={d.key} className="flex items-center gap-2">
                  <span className="w-20 text-sm font-medium text-neutral-700">{d.label}</span>
                  <Input
                    placeholder="9:00 - 22:00"
                    value={form.hours[d.key] || ""}
                    onChange={(e) => updateHours(d.key, e.target.value)}
                    data-testid={`biz-field-hours-${d.key}`}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Section: Photos */}
          <section className="card-base space-y-3">
            <h2 className="font-heading font-semibold text-lg text-agave">Fotos del negocio</h2>
            <p className="text-xs text-neutral-500">
              Sube hasta 6 fotos (interiores, platos, fachada). La primera será la portada.
            </p>
            {form.photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {form.photos.map((p, i) => (
                  <div key={p + i} className="relative aspect-square rounded-xl overflow-hidden group">
                    <img src={p} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() =>
                        updateField(
                          "photos",
                          form.photos.filter((_, idx) => idx !== i),
                        )
                      }
                      className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`biz-photo-remove-${i}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {form.photos.length < 6 && (
              <ImageUpload
                imageType="general"
                onImageUploaded={(url) =>
                  updateField("photos", [...form.photos, url])
                }
              />
            )}
          </section>

          {/* Section: Optional verification */}
          <section className="card-base space-y-3">
            <h2 className="font-heading font-semibold text-lg text-agave">Verificación (opcional)</h2>
            <p className="text-xs text-neutral-500">
              El RFC y el documento de verificación nos ayudan a marcarte como "Socio Verificado" más rápido.
            </p>
            <div>
              <Label htmlFor="biz-rfc">RFC</Label>
              <Input
                id="biz-rfc"
                value={form.rfc}
                onChange={(e) => updateField("rfc", e.target.value.toUpperCase())}
                placeholder="XXX010101XXX"
                data-testid="biz-field-rfc"
              />
            </div>
          </section>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => navigate("/guia")} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="btn-primary"
              data-testid="biz-register-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enviar para revisión
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
